import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Sidebar from '../../components/Sidebar';
import MeditationNav from '../meditation/MeditationNav';
import {
  HiMenu,
  HiPlus,
  HiTrash,
  HiInformationCircle,
  HiChevronDown,
  HiClock,
} from 'react-icons/hi';
import Toast from '../../components/toaster';
import { uploadImage, uploadFile } from '../../services/assetService';
import {
  listMeditationMainCategories,
  listMeditationSubCategories,
} from '../../services/meditationService';
import {
  getForYouDashboardConfig,
  putForYouDashboardConfig,
  validateForYouDashboardConfig,
} from '../../services/forYouDashboardService';
import {
  SECTION_TITLE_MAX,
  buildCategoryAndTitlePayload,
  buildGreetingSlotsPayload,
  emptyGreetingSlot,
  emptySectionTitles,
  normalizeSlotsFromServer,
  sectionTitlesFromConfig,
  validateSectionTitles,
} from './forYouDashboardFormUtils';

const HTTP_URL_RE = /^https?:\/\/.+/i;
const HHMM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

const Spinner = () => (
  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

const FullPageSpinner = () => (
  <div className="fixed inset-0 ml-0 lg:ml-64 flex items-center justify-center z-40">
    <div className="flex items-center gap-3" role="status" aria-live="polite">
      <svg className="w-12 h-12 animate-spin" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="for-you-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#E9D5FF" />
            <stop offset="50%" stopColor="#A855F7" />
            <stop offset="100%" stopColor="#6B21A8" />
          </linearGradient>
        </defs>
        <circle
          cx="50"
          cy="50"
          r="40"
          stroke="url(#for-you-gradient)"
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray="60 200"
          fill="none"
          transform="rotate(-90 50 50)"
        />
      </svg>
    </div>
  </div>
);

const emptyForm = () => ({
  feeling_main_category_id: '',
  quick_sessions_main_category_id: '',
  yoga_nidra_sub_category_id: '',
  deity_affinity_main_category_id: '',
  ...emptySectionTitles(),
  greeting_slots: [emptyGreetingSlot()],
});

const parseHHmmToMinutes = (value) => {
  const m = HHMM_RE.exec(String(value || '').trim());
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
};

/**
 * Client-side schedule check (half-open circular day, 1440 minutes).
 * Mirrors documented Backend semantics. Server remains authoritative.
 */
const validateGreetingSchedule = (slots) => {
  if (!Array.isArray(slots) || slots.length === 0) {
    return 'Add at least one greeting slot covering the full 24 hours.';
  }

  const covered = new Array(1440).fill(0);

  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i];
    const label = `Slot ${i + 1}`;
    if (!String(slot.title || '').trim()) {
      return `${label}: title is required.`;
    }
    if (!String(slot.description || '').trim()) {
      return `${label}: description is required.`;
    }
    const image = String(slot.bg_image || '').trim();
    if (!image) {
      return `${label}: background image URL is required.`;
    }
    if (!HTTP_URL_RE.test(image)) {
      return `${label}: background image must be an HTTP or HTTPS URL.`;
    }
    const audio = String(slot.bg_audio || '').trim();
    if (audio && !HTTP_URL_RE.test(audio)) {
      return `${label}: background audio must be an HTTP or HTTPS URL, or empty.`;
    }

    const start = parseHHmmToMinutes(slot.start_time);
    const end = parseHHmmToMinutes(slot.end_time);
    if (start === null || end === null) {
      return `${label}: start/end must be HH:mm (00:00–23:59).`;
    }

    if (start === end) {
      for (let m = 0; m < 1440; m++) covered[m] += 1;
    } else if (start < end) {
      for (let m = start; m < end; m++) covered[m] += 1;
    } else {
      for (let m = start; m < 1440; m++) covered[m] += 1;
      for (let m = 0; m < end; m++) covered[m] += 1;
    }
  }

  for (let m = 0; m < 1440; m++) {
    if (covered[m] === 0) {
      const hh = String(Math.floor(m / 60)).padStart(2, '0');
      const mm = String(m % 60).padStart(2, '0');
      return `Greeting schedule has a gap around ${hh}:${mm}. Cover the full 24 hours.`;
    }
    if (covered[m] > 1) {
      const hh = String(Math.floor(m / 60)).padStart(2, '0');
      const mm = String(m % 60).padStart(2, '0');
      return `Greeting schedule overlaps around ${hh}:${mm}.`;
    }
  }

  return '';
};

/**
 * Custom category dropdown (matches Meditation Sessions filters — not native <select>).
 */
const CategorySelect = ({
  id,
  label,
  required,
  value,
  options,
  placeholder,
  disabled,
  error,
  open,
  onToggle,
  onSelect,
  getOptionLabel,
  helperText,
}) => {
  const selected = options.find((o) => String(o._id) === String(value));
  const display = selected ? getOptionLabel(selected) : placeholder;

  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-gray-700 mb-1">
        {label}
        {required ? <span className="text-red-500"> *</span> : (
          <span className="font-normal text-gray-400"> (optional)</span>
        )}
      </label>
      <div className="relative for-you-category-dropdown">
        <button
          type="button"
          id={id}
          disabled={disabled}
          onClick={onToggle}
          className={`w-full h-10 pl-3 pr-10 text-sm text-left leading-normal border rounded-xl focus:outline-none bg-white disabled:opacity-60 truncate ${
            error ? 'border-red-300' : 'border-[#ECECEC]'
          }`}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className={selected ? 'text-gray-800' : 'text-gray-400'}>{display}</span>
        </button>
        <HiChevronDown
          className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
          aria-hidden
        />
        {open && (
          <div
            className="absolute z-50 mt-1 left-0 right-0 max-h-60 overflow-y-auto bg-white/95 backdrop-blur-sm rounded-xl shadow-xl border border-gray-200"
            role="listbox"
            aria-labelledby={id}
          >
            {options.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-400">No options available</div>
            ) : (
              options.map((opt) => {
                const isSelected = String(opt._id) === String(value);
                return (
                  <button
                    key={opt._id}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => onSelect(String(opt._id))}
                    className={`w-full px-3 py-2 text-sm text-left hover:bg-gray-100 truncate ${
                      isSelected ? 'bg-gray-50 font-semibold' : ''
                    }`}
                  >
                    {getOptionLabel(opt)}
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
      {helperText && !error ? (
        <p className="mt-1 text-xs text-gray-400">{helperText}</p>
      ) : null}
      {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
    </div>
  );
};

/** Digits-only HH:mm formatter while typing (max 4 digits). */
const formatHHmmInput = (raw) => {
  const digits = String(raw || '')
    .replace(/\D/g, '')
    .slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
};

/** Clamp / pad to valid HH:mm when possible; otherwise leave as typed. */
const normalizeHHmmOnBlur = (raw) => {
  const formatted = formatHHmmInput(raw);
  if (!formatted) return '';
  if (!formatted.includes(':')) {
    if (formatted.length === 1) return `0${formatted}:00`;
    if (formatted.length === 2) return `${formatted}:00`;
    return formatted;
  }
  const [hPart, mPart = ''] = formatted.split(':');
  let h = Number(hPart);
  let m = Number(mPart.padEnd(2, '0').slice(0, 2));
  if (Number.isNaN(h) || Number.isNaN(m)) return formatted;
  if (h > 23) h = 23;
  if (m > 59) m = 59;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const HOURS_24 = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES_60 = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

/**
 * Time field: type digits (auto HH:mm) + branded 24h picker (no AM/PM, no system picker).
 */
const DayTimeField = ({ id, label, value, disabled, onChange, hint }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const hourListRef = useRef(null);
  const minuteListRef = useRef(null);

  const parsed = HHMM_RE.exec(String(value || '').trim());
  const selectedHour = parsed ? parsed[1] : null;
  const selectedMinute = parsed ? parsed[2] : null;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const scrollSelected = (listEl, selected) => {
      if (!listEl || selected == null) return;
      const btn = listEl.querySelector(`[data-value="${selected}"]`);
      if (btn) btn.scrollIntoView({ block: 'center' });
    };
    // Next frame so the list is mounted
    requestAnimationFrame(() => {
      scrollSelected(hourListRef.current, selectedHour || '00');
      scrollSelected(minuteListRef.current, selectedMinute || '00');
    });
  }, [open, selectedHour, selectedMinute]);

  const applyPart = (hour, minute) => {
    const h = hour ?? selectedHour ?? '00';
    const m = minute ?? selectedMinute ?? '00';
    onChange(`${h}:${m}`);
  };

  return (
    <div ref={rootRef} className="relative for-you-time-field">
      <label htmlFor={id} className="block text-xs font-medium text-gray-700 mb-1">
        {label} <span className="text-red-500">*</span>
      </label>
      <div className="relative">
        <input
          id={id}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder="00:00"
          maxLength={5}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(formatHHmmInput(e.target.value))}
          onBlur={() => {
            // Delay so picker clicks aren't wiped by premature normalize
            window.setTimeout(() => {
              if (rootRef.current?.contains(document.activeElement)) return;
              const next = normalizeHHmmOnBlur(value);
              if (next !== value) onChange(next);
            }, 0);
          }}
          className="w-full h-10 pl-3 pr-10 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-gray-500 disabled:opacity-50"
          aria-describedby={hint ? `${id}-hint` : undefined}
        />
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            if (disabled) return;
            setOpen((prev) => !prev);
          }}
          className={`absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md transition-colors disabled:opacity-50 ${
            open
              ? 'text-brand bg-brand/10'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
          }`}
          aria-label={`Open 24-hour time picker for ${label}`}
          aria-expanded={open}
          aria-haspopup="dialog"
        >
          <HiClock className="w-4 h-4" />
        </button>
      </div>

      {open && (
        <div
          className="absolute z-50 mt-1 left-0 right-0 sm:right-auto sm:w-56 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden"
          role="dialog"
          aria-label={`${label} 24-hour time`}
        >
          <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              24-hour
            </span>
            <span className="text-sm font-semibold text-brand tabular-nums">
              {selectedHour ?? '––'}:{selectedMinute ?? '––'}
            </span>
          </div>
          <div className="grid grid-cols-2 divide-x divide-gray-100">
            <div>
              <div className="px-2 py-1 text-[10px] font-semibold text-gray-400 text-center border-b border-gray-50">
                Hour
              </div>
              <div
                ref={hourListRef}
                className="max-h-44 overflow-y-auto py-1"
                role="listbox"
                aria-label="Hour"
              >
                {HOURS_24.map((h) => {
                  const active = h === selectedHour;
                  return (
                    <button
                      key={h}
                      type="button"
                      data-value={h}
                      role="option"
                      aria-selected={active}
                      onClick={() => applyPart(h, null)}
                      className={`w-full px-3 py-1.5 text-sm tabular-nums text-center transition-colors ${
                        active
                          ? 'bg-brand text-white font-semibold'
                          : 'text-gray-700 hover:bg-brand/10'
                      }`}
                    >
                      {h}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="px-2 py-1 text-[10px] font-semibold text-gray-400 text-center border-b border-gray-50">
                Min
              </div>
              <div
                ref={minuteListRef}
                className="max-h-44 overflow-y-auto py-1"
                role="listbox"
                aria-label="Minute"
              >
                {MINUTES_60.map((m) => {
                  const active = m === selectedMinute;
                  return (
                    <button
                      key={m}
                      type="button"
                      data-value={m}
                      role="option"
                      aria-selected={active}
                      onClick={() => applyPart(null, m)}
                      className={`w-full px-3 py-1.5 text-sm tabular-nums text-center transition-colors ${
                        active
                          ? 'bg-brand text-white font-semibold'
                          : 'text-gray-700 hover:bg-brand/10'
                      }`}
                    >
                      {m}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="px-2 py-2 border-t border-gray-100 flex justify-end">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="px-3 py-1 text-xs font-semibold text-white bg-brand rounded-lg hover:opacity-90"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {hint ? (
        <p id={`${id}-hint`} className="mt-1 text-[11px] text-gray-400">
          {hint}
        </p>
      ) : null}
    </div>
  );
};

/**
 * Media URL / file field — same toggle pattern as Meditation CategoryImageField.
 */
const SlotMediaField = ({
  id,
  label,
  value,
  required,
  accept,
  uploadKind,
  disabled,
  onChange,
  onClear,
}) => {
  const [mediaSource, setMediaSource] = useState('url'); // 'url' | 'file'
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [previewFailed, setPreviewFailed] = useState(false);
  const fileRef = useRef(null);

  const trimmed = String(value || '').trim();
  const showClear = Boolean(trimmed) || Boolean(uploadError);
  const canPreviewImage =
    uploadKind === 'image' && trimmed && HTTP_URL_RE.test(trimmed) && !uploadError;

  useEffect(() => {
    setPreviewFailed(false);
  }, [trimmed]);

  const switchSource = (next) => {
    if (uploading || disabled) return;
    setMediaSource(next);
    setUploadError('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || uploading || disabled) return;
    setUploading(true);
    setUploadError('');
    try {
      // Same shared helper as Meditation categories (folder optional per integration guide)
      const url =
        uploadKind === 'image'
          ? await uploadImage(file, { folder: 'ForYouDashboard' })
          : await uploadFile(file, { folder: 'ForYouDashboard' });
      onChange(url);
      setMediaSource('url'); // show the saved HTTPS URL after upload
      setPreviewFailed(false);
    } catch (err) {
      setUploadError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleClear = () => {
    if (uploading || disabled) return;
    setUploadError('');
    setPreviewFailed(false);
    if (fileRef.current) fileRef.current.value = '';
    onClear();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label htmlFor={id} className="block text-xs font-medium text-gray-700">
          {label}
          {required ? <span className="text-red-500"> *</span> : (
            <span className="font-normal text-gray-400"> (optional)</span>
          )}
        </label>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 group relative">
            <HiInformationCircle className="w-4 h-4 text-gray-400 cursor-pointer" />
            <span
              className={`absolute right-0 top-0 -mt-12 sm:-mt-8 px-2 py-1.5 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 sm:whitespace-nowrap leading-tight inline-block ${
                mediaSource === 'url'
                  ? 'w-[185px] sm:w-fit sm:max-w-[290px]'
                  : 'w-[175px] sm:w-fit sm:max-w-[240px]'
              }`}
            >
              {mediaSource === 'url'
                ? 'Switch left to choose a file from your device'
                : 'Switch right to paste a URL.'}
            </span>
          </div>
          <button
            type="button"
            aria-label={
              mediaSource === 'url' ? 'Switch to file upload' : 'Switch to URL'
            }
            disabled={uploading || disabled}
            onClick={() => switchSource(mediaSource === 'url' ? 'file' : 'url')}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${
              mediaSource === 'url' ? 'bg-brand' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm ${
                mediaSource === 'url' ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>
      </div>

      {mediaSource === 'url' ? (
        <div>
          <div className="flex gap-2">
            <input
              id={id}
              type="url"
              value={value}
              disabled={uploading || disabled}
              onChange={(e) => {
                setUploadError('');
                onChange(e.target.value);
              }}
              placeholder="https://…"
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-gray-500 disabled:opacity-50"
            />
            {showClear && (
              <button
                type="button"
                disabled={uploading || disabled}
                onClick={handleClear}
                className="px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap disabled:opacity-50"
              >
                Clear
              </button>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-400">
            {required
              ? 'HTTP/HTTPS URL required.'
              : 'Optional HTTP/HTTPS URL. Clear removes a saved file.'}
          </p>
        </div>
      ) : (
        <div>
          <div className="flex gap-2">
            <input
              ref={fileRef}
              id={`${id}-file`}
              type="file"
              accept={accept}
              disabled={uploading || disabled}
              onChange={handleFile}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            />
            {showClear && (
              <button
                type="button"
                disabled={uploading || disabled}
                onClick={handleClear}
                className="px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap disabled:opacity-50"
              >
                Clear
              </button>
            )}
          </div>
          {uploading && (
            <div className="mt-2 flex items-center gap-2" role="status" aria-live="polite">
              <svg
                className="animate-spin h-4 w-4 text-brand"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="text-xs text-gray-600">
                Uploading {uploadKind === 'image' ? 'image' : 'file'}…
              </span>
            </div>
          )}
          {!uploading && (
            <p className="mt-1 text-xs text-gray-400">
              Choose a file to upload. The URL will appear here after upload.
            </p>
          )}
        </div>
      )}
      {uploadError && <p className="mt-1 text-xs text-red-600">{uploadError}</p>}

      {canPreviewImage && !previewFailed && !uploading && (
        <div className="mt-2">
          <img
            src={trimmed}
            alt="Background preview"
            className="h-20 w-20 object-cover rounded-lg border border-gray-200 bg-gray-50"
            onError={() => setPreviewFailed(true)}
          />
        </div>
      )}
      {canPreviewImage && previewFailed && !uploading && (
        <p className="mt-2 text-xs text-amber-700">
          Preview unavailable — URL may be broken.
        </p>
      )}
      {uploadKind !== 'image' && trimmed && HTTP_URL_RE.test(trimmed) && !uploading && (
        <p className="mt-2 text-xs text-gray-500 truncate" title={trimmed}>
          Saved: {trimmed}
        </p>
      )}
    </div>
  );
};

const ForYouDashboardConfig = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(true);
  const [formData, setFormData] = useState(emptyForm());
  const [scheduleDirty, setScheduleDirty] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [formGeneralError, setFormGeneralError] = useState('');
  const [loadError, setLoadError] = useState(null);
  const [inactiveWarnings, setInactiveWarnings] = useState([]);

  const [mains, setMains] = useState([]);
  const [subs, setSubs] = useState([]);
  const [mainsById, setMainsById] = useState(() => new Map());
  const [catalogsLoading, setCatalogsLoading] = useState(true);
  const [openCategoryDropdown, setOpenCategoryDropdown] = useState(null); // null | 'feeling' | 'quick' | 'yoga'

  const savingRef = useRef(false);

  useEffect(() => {
    if (!openCategoryDropdown) return undefined;
    const onDocClick = (event) => {
      if (!event.target.closest('.for-you-category-dropdown')) {
        setOpenCategoryDropdown(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [openCategoryDropdown]);

  const eligibleSubs = useMemo(
    () =>
      (subs || []).filter((s) => {
        if (!s || s.is_active === false) return false;
        const parentId = String(s.main_category_id || '');
        const parent = mainsById.get(parentId);
        return parent && parent.is_active !== false;
      }),
    [subs, mainsById]
  );

  const applyConfigData = useCallback((data, exists) => {
    if (!exists || !data) {
      setIsCreateMode(true);
      setFormData(emptyForm());
      setScheduleDirty(true);
      setInactiveWarnings([]);
      return;
    }
    setIsCreateMode(false);
    setFormData({
      feeling_main_category_id: data.feeling_main_category_id
        ? String(data.feeling_main_category_id)
        : '',
      quick_sessions_main_category_id: data.quick_sessions_main_category_id
        ? String(data.quick_sessions_main_category_id)
        : '',
      yoga_nidra_sub_category_id: data.yoga_nidra_sub_category_id
        ? String(data.yoga_nidra_sub_category_id)
        : '',
      deity_affinity_main_category_id: data.deity_affinity_main_category_id
        ? String(data.deity_affinity_main_category_id)
        : '',
      ...sectionTitlesFromConfig(data),
      greeting_slots: normalizeSlotsFromServer(data.greeting_slots),
    });
    setScheduleDirty(false);

    const warnings = [];
    if (data.feeling_main_category && data.feeling_main_category.is_active === false) {
      warnings.push(
        `Feeling category “${data.feeling_main_category.title || 'Untitled'}” is inactive — choose another before save if you re-send it.`
      );
    }
    if (
      data.quick_sessions_main_category &&
      data.quick_sessions_main_category.is_active === false
    ) {
      warnings.push(
        `Quick Sessions category “${data.quick_sessions_main_category.title || 'Untitled'}” is inactive — choose another before save if you re-send it.`
      );
    }
    if (data.yoga_nidra_sub_category && data.yoga_nidra_sub_category.is_active === false) {
      warnings.push(
        `Yoga Nidra subcategory “${data.yoga_nidra_sub_category.title || 'Untitled'}” is inactive — choose another before save if you re-send it.`
      );
    }
    if (
      data.yoga_nidra_sub_category?.main_category &&
      data.yoga_nidra_sub_category.main_category.is_active === false
    ) {
      warnings.push(
        `Yoga Nidra parent main “${data.yoga_nidra_sub_category.main_category.title || 'Untitled'}” is inactive.`
      );
    }
    if (
      data.deity_affinity_main_category &&
      data.deity_affinity_main_category.is_active === false
    ) {
      warnings.push(
        `Deity Affinity category “${data.deity_affinity_main_category.title || 'Untitled'}” is inactive — choose another before save if you re-send it.`
      );
    }
    setInactiveWarnings(warnings);
  }, []);

  const loadCatalogs = useCallback(async () => {
    setCatalogsLoading(true);
    try {
      const [mainRes, subRes] = await Promise.all([
        listMeditationMainCategories({ is_active: true }),
        listMeditationSubCategories({ is_active: true }),
      ]);
      const mainRows = Array.isArray(mainRes?.data) ? mainRes.data : [];
      const subRows = Array.isArray(subRes?.data) ? subRes.data : [];
      setMains(mainRows);
      setSubs(subRows);
      const map = new Map();
      mainRows.forEach((m) => {
        if (m?._id) map.set(String(m._id), m);
      });
      setMainsById(map);
    } catch (err) {
      Toast.error(err.message || 'Failed to load Meditation categories');
      setMains([]);
      setSubs([]);
      setMainsById(new Map());
    } finally {
      setCatalogsLoading(false);
    }
  }, []);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setFormGeneralError('');
    try {
      const { exists, data } = await getForYouDashboardConfig();
      applyConfigData(data, exists);
    } catch (err) {
      setLoadError(err.message || 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  }, [applyConfigData]);

  useEffect(() => {
    loadCatalogs();
    loadConfig();
  }, [loadCatalogs, loadConfig]);

  const updateSlot = (index, patch) => {
    setFormData((prev) => {
      const next = [...prev.greeting_slots];
      next[index] = { ...next[index], ...patch };
      return { ...prev, greeting_slots: next };
    });
    setScheduleDirty(true);
  };

  const addSlot = () => {
    setFormData((prev) => ({
      ...prev,
      greeting_slots: [...prev.greeting_slots, emptyGreetingSlot()],
    }));
    setScheduleDirty(true);
  };

  const removeSlot = (index) => {
    setFormData((prev) => {
      if (prev.greeting_slots.length <= 1) return prev;
      return {
        ...prev,
        greeting_slots: prev.greeting_slots.filter((_, i) => i !== index),
      };
    });
    setScheduleDirty(true);
  };

  const buildSavePayload = () => {
    const payload = buildCategoryAndTitlePayload(formData);

    if (isCreateMode || scheduleDirty) {
      payload.greeting_slots = buildGreetingSlotsPayload(formData.greeting_slots);
    }

    return payload;
  };

  const validateFormLocal = () => {
    const errs = {};
    if (!String(formData.feeling_main_category_id || '').trim()) {
      errs.feeling_main_category_id = 'Select a Feeling main category';
    }
    if (!String(formData.quick_sessions_main_category_id || '').trim()) {
      errs.quick_sessions_main_category_id = 'Select a Quick Sessions main category';
    }
    if (!String(formData.yoga_nidra_sub_category_id || '').trim()) {
      errs.yoga_nidra_sub_category_id = 'Select a Yoga Nidra subcategory';
    }
    if (!String(formData.deity_affinity_main_category_id || '').trim()) {
      errs.deity_affinity_main_category_id = 'Select a Deity Affinity main category';
    }

    Object.assign(errs, validateSectionTitles(formData));

    if (isCreateMode || scheduleDirty) {
      const scheduleErr = validateGreetingSchedule(formData.greeting_slots);
      if (scheduleErr) errs.greeting_slots = scheduleErr;
    }

    return errs;
  };

  const handleSave = async () => {
    if (savingRef.current || saving) return;
    const errs = validateFormLocal();
    if (Object.keys(errs).length) {
      setFormErrors(errs);
      setFormGeneralError(errs.greeting_slots || 'Please fix the highlighted fields.');
      return;
    }

    const payload = buildSavePayload();
    savingRef.current = true;
    setSaving(true);
    setFormErrors({});
    setFormGeneralError('');

    try {
      // Optional dry-run — same body as PUT
      await validateForYouDashboardConfig(payload);
      const res = await putForYouDashboardConfig(payload);
      const data = res?.data ?? res;
      applyConfigData(data, true);
      Toast.success(
        isCreateMode
          ? 'For You dashboard configuration created successfully'
          : 'For You dashboard configuration updated successfully'
      );
    } catch (err) {
      setFormGeneralError(err.message || 'Failed to save configuration');
      Toast.error(err.message || 'Failed to save configuration');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const busy = saving || loading || catalogsLoading;

  return (
    <div className="h-screen flex overflow-hidden">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 bg-primary flex flex-col px-4 py-4 md:p-6 overflow-hidden">
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="mb-4 md:mb-6 flex-shrink-0">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-white/50 transition-colors"
                aria-label="Open menu"
              >
                <HiMenu className="w-6 h-6 text-gray-800" />
              </button>
              <div>
                <h1 className="text-xl md:text-3xl font-bold text-gray-800">Meditation</h1>
                <p className="text-sm text-gray-500 mt-0.5">
                  For You Dashboard — Feeling, Quick Sessions, Yoga Nidra &amp; greetings
                </p>
              </div>
            </div>
          </div>

          <MeditationNav />

          {loading || catalogsLoading ? (
            <FullPageSpinner />
          ) : loadError ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center max-w-md">
                <p className="text-red-500 mb-3 whitespace-pre-line">{loadError}</p>
                <button
                  type="button"
                  onClick={loadConfig}
                  className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : (
            <div className="flex-1 min-h-0 overflow-y-auto">
              <div className="bg-white rounded-xl shadow-sm max-w-4xl mx-auto p-4 md:p-6 space-y-6">
                {isCreateMode && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm">
                    <HiInformationCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <p>
                      No configuration yet (GET returned 404). Complete all three category
                      selections and a full-day greeting schedule, then save to create.
                    </p>
                  </div>
                )}

                {inactiveWarnings.length > 0 && (
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm space-y-1">
                    {inactiveWarnings.map((w) => (
                      <p key={w}>{w}</p>
                    ))}
                  </div>
                )}

                {formGeneralError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm whitespace-pre-line">
                    {formGeneralError}
                  </div>
                )}

                {/* Category selections + section titles */}
                <section className="space-y-4">
                  <h2 className="text-sm font-semibold text-gray-800">Content categories</h2>
                  <p className="text-xs text-gray-500">
                    Each category requires an active Meditation category (cannot clear after
                    save — pick a replacement). Section titles are required display copy for
                    the app (max {SECTION_TITLE_MAX} characters).
                  </p>

                  <div className="space-y-3 p-3 rounded-xl border border-[#ECECEC] bg-gray-50/50">
                    <div>
                      <label
                        htmlFor="for-you-feeling-title"
                        className="block text-xs font-medium text-gray-700 mb-1"
                      >
                        How Are You Feeling Right Now? — Section Title
                        <span className="text-red-500"> *</span>
                      </label>
                      <input
                        id="for-you-feeling-title"
                        type="text"
                        maxLength={SECTION_TITLE_MAX}
                        value={formData.feeling_title}
                        disabled={busy}
                        placeholder="Enter feeling section title"
                        onChange={(e) => {
                          setFormData((prev) => ({ ...prev, feeling_title: e.target.value }));
                          setFormErrors((prev) => ({ ...prev, feeling_title: '' }));
                        }}
                        className={`w-full h-10 px-3 text-sm border rounded-xl focus:outline-none bg-white disabled:opacity-60 ${
                          formErrors.feeling_title ? 'border-red-300' : 'border-[#ECECEC]'
                        }`}
                      />
                      {formErrors.feeling_title ? (
                        <p className="mt-1 text-xs text-red-600">{formErrors.feeling_title}</p>
                      ) : null}
                    </div>
                    <CategorySelect
                      id="for-you-feeling-main"
                      label="How Are You Feeling Right Now? (Main Category)"
                      required
                      value={formData.feeling_main_category_id}
                      options={mains}
                      placeholder="Select main category…"
                      disabled={busy}
                      error={formErrors.feeling_main_category_id}
                      open={openCategoryDropdown === 'feeling'}
                      onToggle={() =>
                        setOpenCategoryDropdown((prev) =>
                          prev === 'feeling' ? null : 'feeling'
                        )
                      }
                      onSelect={(id) => {
                        setFormData((prev) => ({
                          ...prev,
                          feeling_main_category_id: id,
                        }));
                        setFormErrors((prev) => ({
                          ...prev,
                          feeling_main_category_id: '',
                        }));
                        setOpenCategoryDropdown(null);
                      }}
                      getOptionLabel={(m) => m.title || 'Untitled'}
                    />
                  </div>

                  <div className="space-y-3 p-3 rounded-xl border border-[#ECECEC] bg-gray-50/50">
                    <div>
                      <label
                        htmlFor="for-you-quick-title"
                        className="block text-xs font-medium text-gray-700 mb-1"
                      >
                        Quick Sessions — Section Title
                        <span className="text-red-500"> *</span>
                      </label>
                      <input
                        id="for-you-quick-title"
                        type="text"
                        maxLength={SECTION_TITLE_MAX}
                        value={formData.quick_sessions_title}
                        disabled={busy}
                        placeholder="Enter quick sessions section title"
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            quick_sessions_title: e.target.value,
                          }));
                          setFormErrors((prev) => ({ ...prev, quick_sessions_title: '' }));
                        }}
                        className={`w-full h-10 px-3 text-sm border rounded-xl focus:outline-none bg-white disabled:opacity-60 ${
                          formErrors.quick_sessions_title
                            ? 'border-red-300'
                            : 'border-[#ECECEC]'
                        }`}
                      />
                      {formErrors.quick_sessions_title ? (
                        <p className="mt-1 text-xs text-red-600">
                          {formErrors.quick_sessions_title}
                        </p>
                      ) : null}
                    </div>
                    <CategorySelect
                      id="for-you-quick-main"
                      label="Quick Sessions (Main Category)"
                      required
                      value={formData.quick_sessions_main_category_id}
                      options={mains}
                      placeholder="Select main category…"
                      disabled={busy}
                      error={formErrors.quick_sessions_main_category_id}
                      open={openCategoryDropdown === 'quick'}
                      onToggle={() =>
                        setOpenCategoryDropdown((prev) =>
                          prev === 'quick' ? null : 'quick'
                        )
                      }
                      onSelect={(id) => {
                        setFormData((prev) => ({
                          ...prev,
                          quick_sessions_main_category_id: id,
                        }));
                        setFormErrors((prev) => ({
                          ...prev,
                          quick_sessions_main_category_id: '',
                        }));
                        setOpenCategoryDropdown(null);
                      }}
                      getOptionLabel={(m) => m.title || 'Untitled'}
                    />
                  </div>

                  <div className="space-y-3 p-3 rounded-xl border border-[#ECECEC] bg-gray-50/50">
                    <div>
                      <label
                        htmlFor="for-you-yoga-title"
                        className="block text-xs font-medium text-gray-700 mb-1"
                      >
                        Yoga Nidra — Section Title
                        <span className="text-red-500"> *</span>
                      </label>
                      <input
                        id="for-you-yoga-title"
                        type="text"
                        maxLength={SECTION_TITLE_MAX}
                        value={formData.yoga_nidra_title}
                        disabled={busy}
                        placeholder="Enter Yoga Nidra section title"
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            yoga_nidra_title: e.target.value,
                          }));
                          setFormErrors((prev) => ({ ...prev, yoga_nidra_title: '' }));
                        }}
                        className={`w-full h-10 px-3 text-sm border rounded-xl focus:outline-none bg-white disabled:opacity-60 ${
                          formErrors.yoga_nidra_title ? 'border-red-300' : 'border-[#ECECEC]'
                        }`}
                      />
                      {formErrors.yoga_nidra_title ? (
                        <p className="mt-1 text-xs text-red-600">{formErrors.yoga_nidra_title}</p>
                      ) : null}
                    </div>
                    <div>
                      <CategorySelect
                        id="for-you-yoga-sub"
                        label="Yoga Nidra (Subcategory)"
                        required
                        value={formData.yoga_nidra_sub_category_id}
                        options={eligibleSubs}
                        placeholder="Select subcategory…"
                        disabled={busy}
                        error={formErrors.yoga_nidra_sub_category_id}
                        open={openCategoryDropdown === 'yoga'}
                        onToggle={() =>
                          setOpenCategoryDropdown((prev) =>
                            prev === 'yoga' ? null : 'yoga'
                          )
                        }
                        onSelect={(id) => {
                          setFormData((prev) => ({
                            ...prev,
                            yoga_nidra_sub_category_id: id,
                          }));
                          setFormErrors((prev) => ({
                            ...prev,
                            yoga_nidra_sub_category_id: '',
                          }));
                          setOpenCategoryDropdown(null);
                        }}
                        getOptionLabel={(s) => {
                          const parent = mainsById.get(String(s.main_category_id || ''));
                          const parentTitle = parent?.title ? ` (${parent.title})` : '';
                          return `${s.title || 'Untitled'}${parentTitle}`;
                        }}
                      />
                      {eligibleSubs.length === 0 && (
                        <p className="mt-1 text-xs text-gray-400">
                          No active subcategories with an active parent main category.
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3 p-3 rounded-xl border border-[#ECECEC] bg-gray-50/50">
                    <div>
                      <label
                        htmlFor="for-you-deity-title"
                        className="block text-xs font-medium text-gray-700 mb-1"
                      >
                        Deity Affinity — Section Title
                        <span className="text-red-500"> *</span>
                      </label>
                      <input
                        id="for-you-deity-title"
                        type="text"
                        maxLength={SECTION_TITLE_MAX}
                        value={formData.deity_affinity_title}
                        disabled={busy}
                        placeholder="Enter deity affinity section title"
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            deity_affinity_title: e.target.value,
                          }));
                          setFormErrors((prev) => ({ ...prev, deity_affinity_title: '' }));
                        }}
                        className={`w-full h-10 px-3 text-sm border rounded-xl focus:outline-none bg-white disabled:opacity-60 ${
                          formErrors.deity_affinity_title
                            ? 'border-red-300'
                            : 'border-[#ECECEC]'
                        }`}
                      />
                      {formErrors.deity_affinity_title ? (
                        <p className="mt-1 text-xs text-red-600">
                          {formErrors.deity_affinity_title}
                        </p>
                      ) : null}
                    </div>
                    <CategorySelect
                      id="for-you-deity-affinity-main"
                      label="Deity Affinity (Main Category)"
                      required
                      value={formData.deity_affinity_main_category_id}
                      options={mains}
                      placeholder="Select main category…"
                      disabled={busy}
                      error={formErrors.deity_affinity_main_category_id}
                      open={openCategoryDropdown === 'deity'}
                      onToggle={() =>
                        setOpenCategoryDropdown((prev) =>
                          prev === 'deity' ? null : 'deity'
                        )
                      }
                      onSelect={(id) => {
                        setFormData((prev) => ({
                          ...prev,
                          deity_affinity_main_category_id: id,
                        }));
                        setFormErrors((prev) => ({
                          ...prev,
                          deity_affinity_main_category_id: '',
                        }));
                        setOpenCategoryDropdown(null);
                      }}
                      getOptionLabel={(m) => m.title || 'Untitled'}
                    />
                  </div>
                </section>

                {/* Greeting schedule */}
                <section className="space-y-4 border-t border-gray-100 pt-6">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h2 className="text-sm font-semibold text-gray-800">
                        Personalized greeting schedule
                      </h2>
                      <p className="text-xs text-gray-500 mt-1">
                        Slot times are local wall-clock (user timezone). Cover 24 hours. Save with
                        slot IDs. Enter <code className="text-[11px]">HH:mm</code> as users see on
                        a clock — do not convert to UTC. No gaps or overlaps.
                      </p>
                      <p className="text-[11px] text-gray-400 mt-1">
                        Example (India): Morning 06:00–12:00, Day 12:00–20:00, Night 20:00–06:00.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={addSlot}
                      disabled={busy}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-brand bg-brand/10 rounded-lg hover:opacity-90 disabled:opacity-50"
                    >
                      <HiPlus className="w-4 h-4" />
                      Add slot
                    </button>
                  </div>

                  {formErrors.greeting_slots && (
                    <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-2">
                      {formErrors.greeting_slots}
                    </p>
                  )}

                  <div className="space-y-4">
                    {formData.greeting_slots.map((slot, index) => (
                      <div
                        key={slot._id || `slot-new-${index}`}
                        className="rounded-xl border border-gray-200 bg-gray-50/50 p-4 space-y-3"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="text-xs font-semibold text-gray-700">
                            Slot {index + 1}
                          </h3>
                          <button
                            type="button"
                            onClick={() => removeSlot(index)}
                            disabled={busy || formData.greeting_slots.length <= 1}
                            className="p-1.5 rounded-lg text-[#F76464] bg-[#FDDDDD] disabled:opacity-40"
                            aria-label={`Remove slot ${index + 1}`}
                          >
                            <HiTrash className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <DayTimeField
                            id={`slot-${index}-start`}
                            label="Start"
                            value={slot.start_time}
                            disabled={busy}
                            onChange={(next) => updateSlot(index, { start_time: next })}
                          />
                          <DayTimeField
                            id={`slot-${index}-end`}
                            label="End"
                            value={slot.end_time}
                            disabled={busy}
                            onChange={(next) => updateSlot(index, { end_time: next })}
                            hint="Same start/end = full 24h. End before start = overnight wrap."
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Title <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={slot.title}
                            disabled={busy}
                            placeholder="e.g. Good morning, {{name}}"
                            onChange={(e) => updateSlot(index, { title: e.target.value })}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none disabled:opacity-50"
                          />
                          <p className="mt-1 text-[11px] text-gray-400">
                            Template: use{' '}
                            <code className="text-[11px] text-gray-500">{'{{name}}'}</code>{' '}
                            — the app replaces it with the user’s name.
                          </p>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Description <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            value={slot.description}
                            disabled={busy}
                            rows={2}
                            placeholder="Optional: include {{name}} for personalization"
                            onChange={(e) =>
                              updateSlot(index, { description: e.target.value })
                            }
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none resize-none disabled:opacity-50"
                          />
                          <p className="mt-1 text-[11px] text-gray-400">
                            Same template —{' '}
                            <code className="text-[11px] text-gray-500">{'{{name}}'}</code>{' '}
                            is replaced with the user’s name in the app.
                          </p>
                        </div>

                        <SlotMediaField
                          id={`slot-${index}-bg-image`}
                          label="Background image"
                          value={slot.bg_image}
                          required
                          accept="image/*"
                          uploadKind="image"
                          disabled={busy}
                          onChange={(next) => updateSlot(index, { bg_image: next })}
                          onClear={() => updateSlot(index, { bg_image: '' })}
                        />

                        <SlotMediaField
                          id={`slot-${index}-bg-audio`}
                          label="Background audio"
                          value={slot.bg_audio}
                          required={false}
                          accept="audio/*"
                          uploadKind="audio"
                          disabled={busy}
                          onChange={(next) => updateSlot(index, { bg_audio: next })}
                          onClear={() => updateSlot(index, { bg_audio: '' })}
                        />
                      </div>
                    ))}
                  </div>
                </section>

                <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
                  <button
                    type="button"
                    onClick={loadConfig}
                    disabled={busy}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={busy}
                    className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                  >
                    {saving ? (
                      <>
                        <Spinner />
                        Saving…
                      </>
                    ) : isCreateMode ? (
                      'Create configuration'
                    ) : (
                      'Save changes'
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForYouDashboardConfig;
